/**
 * Responsibility Types
 * 
 * Per Doc 06 (Queues & Query Truth Table) and Doc 08 (Home Dashboard):
 * - Responsibility classification is server-side canonical truth
 * - UI-side filtering or inference is forbidden for canonical queues
 * - Each item appears in exactly one responsibility queue
 * 
 * Classifications:
 * - REQUIRED: User must act now (valid actions available)
 * - WAITING: Progress blocked on others (no action required)
 * - NONE: Visible for context but not actionable (diagnostics shown)
 */

// ============================================================================
// Classification Types
// ============================================================================

/**
 * Responsibility classification as determined by the server.
 * This is authoritative and must not be recomputed in the UI.
 */
export type ResponsibilityClassification = 'REQUIRED' | 'WAITING' | 'NONE';

/**
 * Scope/source of the responsibility.
 */
export type ResponsibilityScope =
  | 'TASK'
  | 'SESSION'
  | 'CATCHBALL'
  | 'CYCLE'
  | 'PLANNING'
  | 'INTAKE'
  | 'STRATEGY'
  // 981: an assignment of a Registry commitment waiting for the person's acceptance
  | 'COMMITMENT'
  // 1058: a note the person wrote themself, whose reminder has come
  | 'NOTE';

/**
 * What a responsibility is about (migration 820). The subject is the thing the
 * Next-Step Engine evaluates; an ask is finished when its subject no longer
 * needs the person.
 */
export type ResponsibilitySubjectType =
  | 'task'
  | 'session'
  | 'proposal'
  | 'loop'
  | 'ball'
  | 'participation'
  | 'inquiry'
  | 'triage_queue'
  // 945: a V2MOM waiting on the person (review, reopen request, or their own in review)
  | 'v2mom'
  // 945: an open ask to write a V2MOM against a published one
  | 'v2mom_ask'
  // 981: a proposed assignment revision whose assignee is the person or their org
  // unit; subject_id is the revision. Finished when it binds, is withdrawn or superseded.
  | 'assignment_proposed'
  // 1028: a decline, counteroffer or renegotiation waiting on the person who made the
  // request, or a date to get back by that has passed; subject_id is the response.
  | 'assignment_response'
  // 1032: a request past its answer date with no answer, waiting on the person who made
  // it; subject_id is the revision. Finished when it is answered, withdrawn or superseded.
  | 'assignment_unanswered'
  // 1057: a promise of mine in force, due within a week or past due, with nothing said;
  // subject_id is the revision in force. Finished by a response, or when it ends.
  | 'assignment_due'
  // 1057: a promise made to me past its date with nothing said (WAITING).
  | 'assignment_overdue'
  // 1058: a note whose reminder has come; subject_id is the note. Done (complete_note) ends it.
  | 'note';

// ============================================================================
// Validity Types (Migration 107)
// ============================================================================

/**
 * Canonical reasons why a responsibility is invalid/non-actionable.
 * Per Doc 06: This is server-authoritative; UI must not infer validity.
 * 
 * Matches the invalid_reason_code enum in PostgreSQL (migration 107).
 */
export type InvalidReason = 
  | 'MISSING_SESSION'      // task.session_id is NULL
  | 'SESSION_DELETED'      // session no longer exists
  | 'SESSION_COMPLETED'    // session already finished
  | 'SESSION_INACCESSIBLE' // user cannot access session
  | 'EXPIRED'              // task past deadline
  | 'NOT_ASSIGNED'         // user not in participant_ids
  | 'BLOCKED_BY_GATE'      // phase gate prevents action
  | 'PROPOSAL_REJECTED'    // catchball proposal was rejected
  | 'LOOP_CLOSED';         // catchball loop has closed

// ============================================================================
// Action & Diagnostic Types
// ============================================================================

/**
 * A valid action that can be taken on a REQUIRED responsibility.
 * Only present when classification = REQUIRED.
 */
export interface ValidAction {
  /** Action identifier (e.g., 'START_TASK', 'REVIEW_PROPOSAL') */
  action: string;
  /** Human-readable label for the action button */
  label: string;
  /** Route to navigate to for this action */
  route: string;
  /**
   * The RPC that performs this action in one call, when there is one
   * (migration 820). Null means the action is a navigation only.
   */
  rpc?: string | null;
  /** Arguments for that RPC, exactly as the server built them. */
  args?: Record<string, unknown> | null;
}

/**
 * Diagnostic information for NONE or blocked responsibilities.
 * Explains why an item is not actionable.
 */
export interface Diagnostic {
  /** Issue type identifier */
  issue: 
    | 'NO_SESSION'
    | 'SESSION_DELETED'
    | 'SESSION_COMPLETED'
    | 'SESSION_INACCESSIBLE'
    | 'EXPIRED'
    | 'PROPOSAL_REJECTED'
    | 'LOOP_CLOSED'
    | 'WAITING_ON_OTHERS'
    | 'BALL_WITH_OTHERS'
    | 'SESSION_NOT_ACTIVE'
    | string;
  /** Human-readable message describing the issue */
  message: string;
  /** Optional remediation guidance */
  remediation?: string;
}

// ============================================================================
// Main Responsibility Card Type
// ============================================================================

/**
 * A responsibility card as returned by get_responsibilities RPC.
 * 
 * This is the canonical representation of a user's responsibility.
 * The classification is server-authoritative and must be rendered as-is.
 */
export interface ResponsibilityCard {
  /** Unique identifier for this responsibility */
  id: string;
  
  /**
   * Server-determined classification.
   * REQUIRED: User must act now
   * WAITING: Blocked on others
   * NONE: Context only, not actionable
   */
  classification: ResponsibilityClassification;
  
  /** Source/type of this responsibility */
  scope: ResponsibilityScope;

  /** The thing this responsibility is about (migration 820). */
  subject_type?: ResponsibilitySubjectType | string;
  /** Its id. */
  subject_id?: string | null;

  /** The workspace it belongs to, and that workspace's name (migration 867). */
  workspace_id?: string | null;
  workspace_name?: string | null;
  
  /** Human-readable title/subject */
  subject: string;
  
  /** Plain-language explanation of why this responsibility exists */
  why: string;
  
  /** 
   * Deep link to the action surface (nullable).
   * May be null for NONE items with no valid navigation target.
   */
  deep_link: string | null;
  
  /**
   * Valid actions available (only populated for REQUIRED).
   * UI must render these exactly as provided.
   */
  valid_actions: ValidAction[];
  
  /**
   * Diagnostic information (only populated for NONE or blocked items).
   * Explains why the item is not actionable.
   */
  diagnostics: Diagnostic[];
  
  /**
   * Whether this responsibility is valid/actionable.
   * Per Doc 06: This is server-authoritative. UI must not infer validity.
   * 
   * - REQUIRED items: is_valid = true
   * - WAITING items: is_valid = true
   * - NONE items: is_valid = false (with invalid_reason populated)
   */
  is_valid: boolean;
  
  /**
   * If invalid, the reason code explaining why.
   * Null for valid responsibilities (REQUIRED/WAITING).
   * Per Doc 06: This is server-authoritative.
   */
  invalid_reason: InvalidReason | null;
  
  /** When this responsibility was created */
  created_at: string;
  
  /** Optional deadline for this responsibility */
  deadline?: string | null;
  /** 'answer' when the deadline is the date an answer is owed, not the date of the work (1032). */
  deadline_means?: 'answer' | null;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Summary counts returned by get_responsibilities.
 */
export interface ResponsibilitySummary {
  required_count: number;
  waiting_count: number;
  none_count: number;
  total_count: number;
}

/**
 * Full response from get_responsibilities RPC.
 */
export interface ResponsibilitiesResponse {
  /** Array of responsibility cards */
  responsibilities: ResponsibilityCard[];
  /** Summary counts by classification */
  summary: ResponsibilitySummary;
  /** Server timestamp when data was fetched */
  fetched_at: string;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Grouped responsibilities for display purposes.
 * Note: This is display grouping only. The server determined classification;
 * we're just organizing for rendering.
 */
export interface GroupedResponsibilities {
  required: ResponsibilityCard[];
  waiting: ResponsibilityCard[];
  none: ResponsibilityCard[];
}

/**
 * Helper to group responsibilities by classification.
 * Per Doc 06: This is display grouping, NOT reclassification.
 */
export function groupResponsibilities(
  cards: ResponsibilityCard[]
): GroupedResponsibilities {
  return {
    required: cards.filter(c => c.classification === 'REQUIRED'),
    waiting: cards.filter(c => c.classification === 'WAITING'),
    none: cards.filter(c => c.classification === 'NONE'),
  };
}

/**
 * Check if a responsibility has a valid deep link.
 * Used for resilience handling at click time.
 */
export function hasValidDeepLink(card: ResponsibilityCard): boolean {
  return card.deep_link !== null && card.deep_link.length > 0;
}

/**
 * Get the primary action for a REQUIRED responsibility.
 * Returns null if no actions are available.
 */
export function getPrimaryAction(card: ResponsibilityCard): ValidAction | null {
  if (card.classification !== 'REQUIRED' || card.valid_actions.length === 0) {
    return null;
  }
  return card.valid_actions[0];
}

/**
 * Check if a responsibility is valid/actionable.
 * Uses the server-provided is_valid field.
 * 
 * Defaults to true for backwards compatibility if is_valid is not provided
 * (e.g., before migration 107 is applied).
 */
export function isResponsibilityValid(card: ResponsibilityCard): boolean {
  // If is_valid is not provided by the server (migration not applied),
  // default to true for REQUIRED/WAITING, false for NONE
  if (card.is_valid === undefined) {
    return card.classification !== 'NONE';
  }
  return card.is_valid === true;
}

/**
 * Human-readable messages for invalid reasons.
 * Used by UI to display diagnostics.
 */
export const INVALID_REASON_MESSAGES: Record<InvalidReason, string> = {
  MISSING_SESSION: 'Task has no linked session',
  SESSION_DELETED: 'The session was deleted',
  SESSION_COMPLETED: 'The session has already completed',
  SESSION_INACCESSIBLE: 'You cannot access this session',
  EXPIRED: 'The deadline has passed',
  NOT_ASSIGNED: 'You are not assigned to this task',
  BLOCKED_BY_GATE: 'Blocked by a phase gate',
  PROPOSAL_REJECTED: 'The proposal was rejected',
  LOOP_CLOSED: 'The catchball loop has closed',
};

/**
 * Get a human-readable message for an invalid reason.
 */
export function getInvalidReasonMessage(reason: InvalidReason | null | undefined): string {
  if (!reason) return '';
  return INVALID_REASON_MESSAGES[reason] || reason;
}
